import React, { FC, useEffect, useRef, useState } from "react";
import "./StatisticsItems.scss";
import { useDispatchEx, useSelectorEx } from "../../../hooks/redux";
import { asyncGetDailyDealsCountAction, asyncGetFinanceStatsAction } from "../../../redux/slices/finance/financeAction";
import { getColorById } from "../../../helpers/functions";
import { DEBOUNCE_DELAY, TransactionStatsType, TransferMetricKey, TransferTabKey } from "./statisticsConfig";
import SalarySection from "./SalarySection";
import ExpensesSection from "./ExpensesSection";
import TransferSection from "./TransferSection";
import PreloaderContent from "../../atoms/PreloaderContent/PreloaderContent";
import FinanceService from "../../../api/FinanceService";
import { IDailyDeal } from "./TransferSection";

interface StatisticsItemsProps {
    type: TransactionStatsType;
}

const DEFAULT_TRANSFER_CURRENCY = "USDT";
const DEFAULT_PURCHASE_AMOUNT_CURRENCY = "USDT";

const StatisticsItems: FC<StatisticsItemsProps> = ({ type }) => {
    const dispatch = useDispatchEx();
    const { stats, isStatsLoading, dailyDealsCount } = useSelectorEx((state: any) => state.finance);

    // --- State: Salary ---
    const [salaryDateFrom, setSalaryDateFrom] = useState<string>("");
    const [salaryDateTo, setSalaryDateTo] = useState<string>("");

    // --- State: Expenses ---
    const [expensesDateFrom, setExpensesDateFrom] = useState<string>("");
    const [expensesDateTo, setExpensesDateTo] = useState<string>("");

    // --- State: Transfer (Tabs & Filters) ---
    const [transferTab, setTransferTab] = useState<TransferTabKey>("OVERALL");
    const [transferMetric, setTransferMetric] = useState<TransferMetricKey>("rub_amount");

    const [overallDateFrom, setOverallDateFrom] = useState<string>("");
    const [overallDateTo, setOverallDateTo] = useState<string>("");

    const [currencyDateFrom, setCurrencyDateFrom] = useState<string>("");
    const [currencyDateTo, setCurrencyDateTo] = useState<string>("");

    const [selectedCurrency, setSelectedCurrency] = useState<string>(DEFAULT_TRANSFER_CURRENCY);

    // --- State: Transfer (Chart Controls) ---
    const [dailyDealsPeriod, setDailyDealsPeriod] = useState<"month" | "year">("month");
    const [dailyDealsYear, setDailyDealsYear] = useState<number>(new Date().getFullYear());
    const [dailyDealsMonth, setDailyDealsMonth] = useState<number>(new Date().getMonth());

    // --- State: Available Years (from Backend) ---
    const [availableYears, setAvailableYears] = useState<number[]>([]);

    // --- State: Yearly deals — отдельный запрос, всегда за полный год ---
    const [yearlyDeals, setYearlyDeals] = useState<IDailyDeal[] | null>(null);

    const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // 1. Загрузка доступных лет
    useEffect(() => {
        if (type === "TRANSFER") {
            FinanceService.getAvailableYears()
                .then((res) => {
                    if (Array.isArray(res.data)) {
                        setAvailableYears(res.data);
                    }
                })
                .catch((e) => {
                    console.error("Failed to load available years:", e);
                    setAvailableYears([new Date().getFullYear()]);
                });
        }
    }, [type]);

    // 2. Загрузка данных графика (текущий период: месяц или год)
    useEffect(() => {
        if (type === "TRANSFER") {
            dispatch(asyncGetDailyDealsCountAction({
                period: dailyDealsPeriod,
                year: dailyDealsYear,
                month: dailyDealsMonth
            }) as any);
        }
    }, [dispatch, type, dailyDealsPeriod, dailyDealsYear, dailyDealsMonth]);

    // 3. Отдельная загрузка годовых данных при смене года —
    //    нужна для плашки "за год" когда график в режиме "По дням"
    useEffect(() => {
        if (type !== "TRANSFER") return;

        FinanceService.getDailyDealsCount("year", dailyDealsYear)
            .then((res) => {
                if (Array.isArray(res.data)) {
                    setYearlyDeals(res.data);
                }
            })
            .catch((e) => {
                console.error("Failed to load yearly deals:", e);
                setYearlyDeals(null);
            });
    }, [type, dailyDealsYear]);

    // 4. Дебаунс для загрузки основной статистики
    useEffect(() => {
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

        debounceTimeoutRef.current = setTimeout(() => {
            if (type === "SALARY") {
                dispatch(
                    asyncGetFinanceStatsAction({
                        dateFrom: salaryDateFrom || undefined,
                        dateTo: salaryDateTo || undefined
                    } as any)
                );
            }

            if (type === "EXPENSES") {
                dispatch(
                    asyncGetFinanceStatsAction({
                        dateFrom: expensesDateFrom || undefined,
                        dateTo: expensesDateTo || undefined
                    } as any)
                );
            }

            if (type === "TRANSFER") {
                if (transferTab === "OVERALL") {
                    dispatch(
                        asyncGetFinanceStatsAction({
                            dateFrom: overallDateFrom || undefined,
                            dateTo: overallDateTo || undefined,
                            purchase_amount_currency:
                                transferMetric === "purchase_amount" || transferMetric === "send_amount"
                                    ? DEFAULT_PURCHASE_AMOUNT_CURRENCY
                                    : undefined
                        } as any)
                    );
                }

                if (transferTab === "CURRENCY") {
                    dispatch(
                        asyncGetFinanceStatsAction({
                            dateFrom: currencyDateFrom || undefined,
                            dateTo: currencyDateTo || undefined,
                            purchase_currency: selectedCurrency || undefined,
                            purchase_amount_currency: DEFAULT_PURCHASE_AMOUNT_CURRENCY
                        } as any)
                    );
                }
            }
        }, DEBOUNCE_DELAY);

        return () => {
            if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
        };
    }, [
        dispatch,
        type,
        salaryDateFrom,
        salaryDateTo,
        expensesDateFrom,
        expensesDateTo,
        transferTab,
        transferMetric,
        overallDateFrom,
        overallDateTo,
        currencyDateFrom,
        currencyDateTo,
        selectedCurrency
    ]);

    const colorConfig = type === "SALARY" ? getColorById(3) : type === "TRANSFER" ? getColorById(4) : getColorById(5);
    const linkTo = "/admin/finance";

    if (isStatsLoading && !stats) {
        return (
            <div className="statistics-items">
                <div className="statistics-items__loading">
                    <PreloaderContent type={"relative"} />
                </div>
            </div>
        );
    }

    if (!stats) {
        return (
            <div className="statistics-items">
                <div className="statistics-items__loading">Нет данных для отображения</div>
            </div>
        );
    }

    return (
        <div className="statistics-items">
            {type === "SALARY" && (
                <SalarySection
                    stats={stats}
                    linkTo={linkTo}
                    colorConfig={colorConfig}
                    salaryDateFrom={salaryDateFrom}
                    salaryDateTo={salaryDateTo}
                    setSalaryDateFrom={setSalaryDateFrom}
                    setSalaryDateTo={setSalaryDateTo}
                    onReset={() => {
                        setSalaryDateFrom("");
                        setSalaryDateTo("");
                    }}
                />
            )}

            {type === "TRANSFER" && (
                <TransferSection
                    stats={stats}
                    dailyDeals={dailyDealsCount}
                    yearlyDeals={yearlyDeals}
                    linkTo={linkTo}
                    colorConfig={colorConfig}

                    transferTab={transferTab}
                    setTransferTab={setTransferTab}
                    transferMetric={transferMetric}
                    setTransferMetric={setTransferMetric}

                    overallDateFrom={overallDateFrom}
                    overallDateTo={overallDateTo}
                    setOverallDateFrom={setOverallDateFrom}
                    setOverallDateTo={setOverallDateTo}

                    currencyDateFrom={currencyDateFrom}
                    currencyDateTo={currencyDateTo}
                    setCurrencyDateFrom={setCurrencyDateFrom}
                    setCurrencyDateTo={setCurrencyDateTo}
                    selectedCurrency={selectedCurrency}
                    setSelectedCurrency={setSelectedCurrency}

                    dailyDealsPeriod={dailyDealsPeriod}
                    setDailyDealsPeriod={setDailyDealsPeriod}
                    dailyDealsYear={dailyDealsYear}
                    setDailyDealsYear={setDailyDealsYear}
                    dailyDealsMonth={dailyDealsMonth}
                    setDailyDealsMonth={setDailyDealsMonth}

                    availableYears={availableYears}

                    onResetOverall={() => {
                        setOverallDateFrom("");
                        setOverallDateTo("");
                        setTransferMetric("rub_amount");
                        setDailyDealsPeriod("month");
                        setDailyDealsYear(new Date().getFullYear());
                        setDailyDealsMonth(new Date().getMonth());
                    }}
                    onResetCurrency={() => {
                        setCurrencyDateFrom("");
                        setCurrencyDateTo("");
                        setSelectedCurrency(DEFAULT_TRANSFER_CURRENCY);
                    }}
                />
            )}

            {type === "EXPENSES" && (
                <ExpensesSection
                    stats={stats}
                    linkTo={linkTo}
                    colorConfig={colorConfig}
                    expensesDateFrom={expensesDateFrom}
                    expensesDateTo={expensesDateTo}
                    setExpensesDateFrom={setExpensesDateFrom}
                    setExpensesDateTo={setExpensesDateTo}
                    onReset={() => {
                        setExpensesDateFrom("");
                        setExpensesDateTo("");
                    }}
                />
            )}
        </div>
    );
};

export default StatisticsItems;